import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  api,
  type Candidate,
  type MatchDetail,
  type MatchSummary,
  type RegionEvents,
  type StatusResponse,
  type StoredMatch,
  type WindowInfo,
} from "./api";
import { baseEventId, groupEvents, sortRegions, type GroupedEvent } from "./events";

const STATUS_POLL_MS = 5000;
const REGION_KEY = "wp.region";
const ADMIN_KEY = "wp.admin";
const MERYTZ_KEY = "wp.merytz";
const WATCHED_KEY = "wp:watched";

/** A new visitor's starting "my players": the headliner duos, so the
 *  dashboard is alive on first load. These mirror the server's mandated
 *  capture floor (players.default_watched in apps/server, re-asserted on
 *  every boot), so the default view is always backed by real capture. Keep
 *  this list in sync with that server default. */
const DEFAULT_WATCHED: string[] = [
  "45ce4565b6db452c9419119b12e7eac3", // Peterbot
  "9868429b133a42b8a43f737887da4673", // Pollo
  "827abb1cd9fb4618991425c2d3ba9b76", // Bugha
  "c17bac91e57e4ab58cc5c4e9d846afc5", // Braydz
];


function loadSet(key: string): Set<string> {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}
function saveSet(key: string, set: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    /* storage unavailable — just won't persist */
  }
}

/** A grouped event the dashboard is allowed to show: it is being watched
 *  right now and/or has parsed lobbies in the archive. */
export interface TrackedEvent {
  group: GroupedEvent;
  stored: StoredMatch[];
  watchedWindows: WindowInfo[];
  /** a poller is currently running for one of its windows */
  isWatched: boolean;
}

interface AppState {
  status: StatusResponse | null;
  offline: boolean;
  grouped: GroupedEvent[] | null;
  eventsError: string | null;
  /** the one region the whole app is scoped to (persisted) */
  region: string;
  setRegion: (region: string) => void;
  /** regions that actually have events in the catalog, in display order */
  availableRegions: string[];
  /** ops affordances (watch/unwatch/backfill, raw ids) are hidden unless on */
  admin: boolean;
  setAdmin: (on: boolean) => void;
  /** unlocks only the new-pro (add by epic id) form, flipped via ?merytz=1 */
  merytz: boolean;
  /** base_id → archive matches; null until first load finishes */
  storedByEvent: Map<string, StoredMatch[]> | null;
  tracked: TrackedEvent[] | null;
  refreshStatus: () => Promise<void>;
  /** roster drawer */
  openMatch: (sessionId: string) => void;
  /** re-fetch the open match without clearing it (progress polling) */
  refreshMatch: () => void;
  closeMatch: () => void;
  selectedSession: string | null;
  matchDetail: MatchDetail | null;
  matchLoading: boolean;
  /** the client's "my players" — epic ids, persisted in localStorage */
  watchedIds: string[];
  isWatched: (epicId: string) => boolean;
  /** add a player: localStorage + POST (joins the server capture set) */
  watchPlayer: (epicId: string) => void;
  /** remove from this browser's dashboard only (server set is curated separately) */
  unwatchPlayer: (epicId: string) => void;
  /** the verified-pro pick list, fetched once */
  candidates: Candidate[];
  /** admin: add a new verified pro to the allowlist, then return it so the
   *  caller can immediately watch them. Updates the local pick list too. */
  addCandidate: (epicId: string, username: string, region: string) => Promise<Candidate>;
  /** a live session id is "new" until its id has been copied */
  isCopied: (sessionId: string) => boolean;
  markCopied: (sessionId: string) => void;
}

const AppContext = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [offline, setOffline] = useState(false);
  const [regions, setRegions] = useState<RegionEvents[] | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [storedByEvent, setStoredByEvent] = useState<Map<string, StoredMatch[]> | null>(null);

  // ── region: a single persisted value shared by every page ──
  const [region, setRegionState] = useState(() => {
    try {
      return localStorage.getItem(REGION_KEY) ?? "NAC";
    } catch {
      return "NAC";
    }
  });
  const setRegion = useCallback((r: string) => {
    setRegionState(r);
    try {
      localStorage.setItem(REGION_KEY, r);
    } catch {
      /* storage unavailable — just won't persist */
    }
  }, []);

  // ── admin: hidden ops mode, flipped via ?admin=1 then sticky ──
  const [admin, setAdminState] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has("admin")) {
        const v = params.get("admin");
        const on = v !== "0" && v !== "false";
        localStorage.setItem(ADMIN_KEY, on ? "1" : "");
        return on;
      }
      return localStorage.getItem(ADMIN_KEY) === "1";
    } catch {
      return false;
    }
  });
  const setAdmin = useCallback((on: boolean) => {
    setAdminState(on);
    try {
      localStorage.setItem(ADMIN_KEY, on ? "1" : "");
    } catch {
      /* storage unavailable */
    }
  }, []);

  // ── merytz: unlocks just the new-pro form, flipped via ?merytz=1 then sticky ──
  const [merytz] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has("merytz")) {
        const v = params.get("merytz");
        const on = v !== "0" && v !== "false";
        localStorage.setItem(MERYTZ_KEY, on ? "1" : "");
        return on;
      }
      return localStorage.getItem(MERYTZ_KEY) === "1";
    } catch {
      return false;
    }
  });

  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [matchDetail, setMatchDetail] = useState<MatchDetail | null>(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selectedSession;

  // ── watch set ("my players", persisted) ──
  // First visit (no key yet) seeds the headliner defaults; afterwards the
  // stored set is authoritative even when empty.
  const [watched, setWatched] = useState<Set<string>>(() => {
    try {
      const raw =
        typeof localStorage !== "undefined" ? localStorage.getItem(WATCHED_KEY) : null;
      return new Set(raw == null ? DEFAULT_WATCHED : (JSON.parse(raw) as string[]));
    } catch {
      return new Set(DEFAULT_WATCHED);
    }
  });
  useEffect(() => saveSet(WATCHED_KEY, watched), [watched]);

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  useEffect(() => {
    let cancelled = false;
    api
      .candidates()
      .then(r => {
        if (!cancelled) setCandidates(r.candidates);
      })
      .catch(() => {
        /* the picker falls back to live search */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const addCandidate = useCallback(
    async (epicId: string, username: string, region: string) => {
      const c = await api.addCandidate(epicId, username, region);
      // Merge into the cached pick list (replace any existing row for this
      // id) so the picker shows them without a refetch.
      setCandidates(prev => [...prev.filter(p => p.epic_id !== c.epic_id), c]);
      return c;
    },
    [],
  );

  const isWatched = useCallback((epicId: string) => watched.has(epicId), [watched]);
  const watchPlayer = useCallback((epicId: string) => {
    setWatched(s => (s.has(epicId) ? s : new Set(s).add(epicId)));
    // Deliberate watch → join the server capture set (fire-and-forget).
    api.watch(epicId).catch(() => {});
  }, []);
  const unwatchPlayer = useCallback((epicId: string) => {
    // Browser-only: the server capture set is curated separately.
    setWatched(s => {
      if (!s.has(epicId)) return s;
      const n = new Set(s);
      n.delete(epicId);
      return n;
    });
  }, []);
  const watchedIds = useMemo(() => [...watched], [watched]);

  const [copiedIds, setCopiedIds] = useState<Set<string>>(() => loadSet("wp:copied"));
  useEffect(() => saveSet("wp:copied", copiedIds), [copiedIds]);
  const isCopied = useCallback((id: string) => copiedIds.has(id), [copiedIds]);
  const markCopied = useCallback((id: string) => {
    setCopiedIds(c => (c.has(id) ? c : new Set(c).add(id)));
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      setStatus(await api.status());
      setOffline(false);
    } catch {
      setOffline(true);
    }
  }, []);

  useEffect(() => {
    refreshStatus();
    const t = setInterval(refreshStatus, STATUS_POLL_MS);
    return () => clearInterval(t);
  }, [refreshStatus]);

  useEffect(() => {
    api
      .events()
      .then(r => {
        setRegions(r.regions);
        setEventsError(null);
      })
      .catch(err =>
        setEventsError(err instanceof Error ? err.message : "failed to load events"),
      );
  }, []);

  const grouped = useMemo(
    () => (regions ? groupEvents(regions, Date.now()) : null),
    [regions],
  );

  const availableRegions = useMemo(() => {
    const seen = new Set<string>();
    for (const g of grouped ?? []) for (const r of g.regions) seen.add(r.region);
    return sortRegions(seen);
  }, [grouped]);

  // Only correct a region that genuinely isn't in the catalog (e.g. a stale
  // localStorage value). A region the user picked from the nav is always in
  // `availableRegions`, so this never fights a deliberate choice.
  useEffect(() => {
    if (availableRegions.length > 0 && !availableRegions.includes(region)) {
      setRegion(availableRegions[0]!);
    }
  }, [availableRegions, region, setRegion]);

  // Re-pull the archive whenever a lobby finishes parsing (the done-count
  // moves) — that is the only moment stored matches actually change. One
  // aggregate request, grouped by event base here; per-event fetches
  // hammered the server with requests for every event in the catalog.
  const doneCount = status?.matches.filter(m => m.status === "done").length ?? 0;
  useEffect(() => {
    if (!grouped) return;
    let cancelled = false;
    api
      .archiveMatches()
      .then(r => {
        if (cancelled) return;
        const map = new Map<string, StoredMatch[]>();
        for (const m of r.matches) {
          const base = baseEventId(m.event_id);
          map.set(base, [...(map.get(base) ?? []), m]);
        }
        setStoredByEvent(map);
      })
      .catch(() => {
        if (!cancelled) setStoredByEvent(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [grouped, doneCount]);

  const tracked = useMemo<TrackedEvent[] | null>(() => {
    if (!grouped || !storedByEvent) return null;
    const watchedByBase = new Map<string, WindowInfo[]>();
    for (const w of status?.windows ?? []) {
      const base = baseEventId(w.event_id);
      watchedByBase.set(base, [...(watchedByBase.get(base) ?? []), w]);
    }
    return grouped
      .map(group => {
        const stored = storedByEvent.get(group.base_id) ?? [];
        const watchedWindows = watchedByBase.get(group.base_id) ?? [];
        return { group, stored, watchedWindows, isWatched: watchedWindows.length > 0 };
      })
      .filter(t => t.isWatched || t.stored.length > 0)
      .sort((a, b) => {
        if (a.isWatched !== b.isWatched) return a.isWatched ? -1 : 1;
        const lastA = a.stored[0]?.completed_at ?? "";
        const lastB = b.stored[0]?.completed_at ?? "";
        return lastB.localeCompare(lastA);
      });
  }, [grouped, storedByEvent, status]);

  const fetchDetail = useCallback(async (sessionId: string) => {
    try {
      const d = await api.match(sessionId);
      if (selectedRef.current === sessionId) setMatchDetail(d);
    } catch {
      // drawer shows its own empty state
    }
  }, []);

  const openMatch = useCallback(
    async (sessionId: string) => {
      setSelectedSession(sessionId);
      setMatchLoading(true);
      setMatchDetail(null);
      await fetchDetail(sessionId);
      setMatchLoading(false);
    },
    [fetchDetail],
  );

  const refreshMatch = useCallback(() => {
    if (selectedRef.current) fetchDetail(selectedRef.current);
  }, [fetchDetail]);

  const closeMatch = useCallback(() => {
    setSelectedSession(null);
    setMatchDetail(null);
  }, []);

  const value: AppState = {
    status,
    offline,
    grouped,
    eventsError,
    region,
    setRegion,
    availableRegions,
    admin,
    setAdmin,
    merytz,
    storedByEvent,
    tracked,
    refreshStatus,
    openMatch,
    refreshMatch,
    closeMatch,
    selectedSession,
    matchDetail,
    matchLoading,
    watchedIds,
    isWatched,
    watchPlayer,
    unwatchPlayer,
    candidates,
    addCandidate,
    isCopied,
    markCopied,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppState {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp outside AppProvider");
  return ctx;
}

export function liveMatchesFor(
  status: StatusResponse | null,
  windowIds: Set<string>,
): MatchSummary[] {
  return (status?.matches ?? []).filter(m => windowIds.has(m.event_window_id));
}
